import React from 'react';
import UserStore from '../stores/UserStore';
import UserConstants from '../constants/UserConstants';
import UserActions from '../actions/UserActions';
import UserSelect from './UserSelect';
import ChangeUserPassword from './ChangeUserPassword';
import UserPermissionNames from '../constants/UserPermissionNames';
import AuthenticationModeEnum from '../constants/AuthenticationModeEnum';
import ExternalProviderEnum from '../constants/ExternalProviderEnum';
import * as Inovx from '@shared';
var SystemActions = Inovx.Shared.System.Actions;
var SystemStore = Inovx.Shared.System.Store;

var InovxControls = Inovx.Shared.Controls;
var PermissionService = Inovx.Shared.Permission.PermissionService;

declare var InovxLoader: any;

interface UserFormProps {

}

interface UserFormState {
    editUser?: IUserDetails | any;
    isAdding?: boolean;
    isEditing?: boolean;
    userHints?: IUser[];
    displaySelectUser?: boolean;
    displayPasswordChange?: boolean;
    isActionInProgress?: boolean;
    actionInProgressMsg?: string;
    validation?: IValidationDictionaryObject;
    authenticationMode: string;
    externalProviderName: string;
    hasRolePermission?: boolean;
    hasEntitlementPermission?: boolean;
    roles?: IRole[];
    entitlement?: IEntitlementGroup[];
    isValidADUserID: boolean;
    useExternalAccount: boolean;
}

function getActionInProgress(): boolean {
    return SystemStore.isActionInProgress(UserConstants.USER_ADD) ||
        SystemStore.isActionInProgress(UserConstants.USER_EDIT) ||
        SystemStore.isActionInProgress(UserConstants.USER_CHECK_NAME);
}

var _isValidADUserID: boolean;
var _displaySelectUserState: boolean;
var _displayPasswordChange: boolean;
var _actionInProgressMsg: string;
var _useExternalAccount: boolean;

function getFormState(): UserFormState {

    // We need to get a copy of the user object here as the store does modifications
    // to the ID arrays directly and that would make the detection of any state
    /// changes impossible.
    var user = UserStore.getEditUserCopy();

    return {
        editUser: user,
        isAdding: UserStore.getIsAdding(),
        isEditing: UserStore.getIsEditing(),
        displaySelectUser: _displaySelectUserState,
        displayPasswordChange: _displayPasswordChange,
        isActionInProgress: getActionInProgress(),
        actionInProgressMsg: _actionInProgressMsg,
        validation: UserStore.getValidation(UserConstants.USER_ADD_FORM),
        authenticationMode: UserStore.getSettings().AuthenticationMode,
        externalProviderName: UserStore.getSettings().ExternalProviderName,
        hasEntitlementPermission: PermissionService.checkPermission(UserPermissionNames.ENTITLEMENT_MANAGE),
        hasRolePermission: PermissionService.checkPermission(UserPermissionNames.ROLE_MANAGE),
        entitlement: UserStore.getValidEntitlements(),
        roles: UserStore.getValidRoles(),
        isValidADUserID: _isValidADUserID,
        useExternalAccount: _useExternalAccount
    };
}

class UserForm extends React.Component<UserFormProps, UserFormState> {

    _linkedEntitlements: IEntitlementGroup[] = [];
    _linkedRoles: IRole[] = [];

    constructor(props: UserFormProps) {
        super(props);

        _displaySelectUserState = false;
        _displayPasswordChange = false;
        _useExternalAccount = true;
        _isValidADUserID = UserStore.getIsEditing();

        this.state = getFormState();
        this._linkedEntitlements = this.state.entitlement.filter(group => group.IsLinkedGroup);
        this._linkedRoles = this.state.roles.filter(role => role.IsLinkedRole);
    }

    private _onChange = (event: any) => {
        UserActions.editCurrentObject(UserConstants.USER_ADD_FORM, event.target.name, event.target.value);
        if (event.target.name == "ExternalAccountUserName") {
            UserActions.editCurrentObject(UserConstants.USER_ADD_FORM, "ExternalAccountID", null);
            _isValidADUserID = false;
            this.setState({ isValidADUserID: false });
        }
    }

    private _onUseExternalAccountChange = (event: any) => {
        var value = false;
        if (event.target.checked) {
            value = true;
            _useExternalAccount = true;
        } else {
            UserActions.editCurrentObject(UserConstants.USER_ADD_FORM, "ExternalAccountID", "");
            UserActions.editCurrentObject(UserConstants.USER_ADD_FORM, "ExternalAccountUserName", "");
            _useExternalAccount = false;
        }

        this.setState({ useExternalAccount: value });
    }

    private _canSelectRole = (roleObj: INamedItem) => {
        const isLinkedRole = this._linkedRoles.some(role => role.ID === roleObj.ID);

        if (isLinkedRole) {
            var messageObject = {
                message: InovxLoader.t('users:message.linkedRoleCannotAddOrRemove')
            }
            SystemActions.displayWarningMessage(messageObject);
            return false;
        }

        return true;
    }

    private _canSelectEntitlement = (entitlementObj: INamedItem) => {
        const isLinkedGroup = this._linkedEntitlements.some(group => group.ID === entitlementObj.ID);

        if (isLinkedGroup) {
            var messageObject = {
                message: InovxLoader.t('users:message.linkedGroupCannotAddOrRemove')
            }
            SystemActions.displayWarningMessage(messageObject);
            return false;
        }

        return true;
    }

    private _onRolesSelect = (roleObj: INamedItem, selected: boolean) => {
        this._onSelect('RoleIDs', roleObj, selected);
    }

    private _onEntitlementSelect = (entitlementObj: INamedItem, selected: boolean) => {
        this._onSelect('EntitlementGroupIDs', entitlementObj, selected);
    }

    private _onSelect = (name: string, obj: INamedItem, selected: boolean) => {
        if (selected) {
            UserActions.editCurrentObjectAddToList(UserConstants.USER_ADD_FORM, name, obj.ID);
        } else {
            UserActions.editCurrentObjectRemoveFromList(UserConstants.USER_ADD_FORM, name, obj.ID);
        }
    }

    private _onCheckChange = (event: any) => {
        UserActions.editCurrentObject(UserConstants.USER_ADD_FORM, event.target.name, event.target.value === "true");
    }

    private _loadState = () => {
        this.setState(getFormState());
    }

    private _systemChange = () => {
        this.setState({
            isActionInProgress: getActionInProgress()
        });
    }

    private _saveUser = () => {
        if (!this.state.isValidADUserID && this.state.authenticationMode != AuthenticationModeEnum.LocalOnly && this.state.useExternalAccount) {
            var checkMessage = InovxLoader.t('users:message.checkAzureADUser');
            if (this.state.externalProviderName == ExternalProviderEnum.ADFS) {
                checkMessage = InovxLoader.t('users:message.checkAdfsUser');
            }
            var messageObject = {
                message: checkMessage
            }
            SystemActions.displayWarningMessage(messageObject);
        }
        else
        {
            if (this.state.isAdding) {
                UserActions.create();
            } else {
                UserActions.edit();
            }
            this.setState({ actionInProgressMsg: InovxLoader.t('users:form.saving') });
        }

    }

    private _cancel = () => {
        UserActions.finishEdit();
    }

    private _checkName = () => {
        if (this.state.hasEntitlementPermission && this.state.hasRolePermission) {
            this.setState({ displaySelectUser: true });
        }
        else {
            SystemActions.displayErrorMessage({
                message: InovxLoader.t('users:message.missingSearchPermissions')
            });
        }
    }

    private _closeSelectUser = () => {
        _displaySelectUserState = false;
        this.setState({
            userHints: [],
            displaySelectUser: _displaySelectUserState
        });
    }

    private _closeChangePassword = () => {
        _displayPasswordChange = false;
        this.setState({
            displayPasswordChange: _displayPasswordChange
        });
    }

    private _onChangePassword = () => {
        _displayPasswordChange = true;
        this.setState({
            displayPasswordChange: _displayPasswordChange
        });
    }

    private _onResetLoginAttempts = () => {
        UserActions.resetLoginAttempts(this.state.editUser);
    }

    private _fillUser = (user: IUser) => {
        if (user && user.UserID) {
            _displaySelectUserState = false;

            Promise.all([UserActions.getLinkedEntitlementGroups(user.UserID), UserActions.getLinkedRoles(user.UserID)])
                .then(([grpData, roleData]) => {
                    // Process the data once both requests are finished

                    this._removeExistLinkedEntitlementGroups();
                    this._addLinkedEntitlementGroups(grpData);

                    this._removeExistLinkedRoles();
                    this._addLinkedRoles(roleData);

                    if (this.state.isEditing) {
                        UserActions.editCurrentObject(UserConstants.USER_ADD_FORM, "ExternalAccountUserName", user.UserName);
                        UserActions.editCurrentObject(UserConstants.USER_ADD_FORM, "ExternalAccountID", user.UserID);
                    } else {
                        UserActions.selectHint(user);
                        UserActions.editCurrentObject(UserConstants.USER_ADD_FORM, "ExternalAccountID", user.UserID);
                    }
                    _isValidADUserID = true;
                    this.setState({ isValidADUserID: true });
                })
                .catch(error => {
                    var messageObject = {
                        message: InovxLoader.t('users:message.linkedEntitlementGroupsFailed')
                    }
                    SystemActions.displayErrorMessage(messageObject);
                });
        }
    }

    private _addLinkedEntitlementGroups = (externalGroupIDs: string[]) => {
        this._linkedEntitlements.filter(group => externalGroupIDs.includes(group.ID)).forEach(group => {
            UserActions.editCurrentObjectAddToList(UserConstants.USER_ADD_FORM, 'EntitlementGroupIDs', group.ID);
        });
    }

    private _removeExistLinkedEntitlementGroups = () => {
        if (this.state.editUser) {
            this._linkedEntitlements.filter(group => this.state.editUser.EntitlementGroupIDs.includes(group.ID)).forEach(group => {
                UserActions.editCurrentObjectRemoveFromList(UserConstants.USER_ADD_FORM, 'EntitlementGroupIDs', group.ID);
            });
        }
    }

    private _addLinkedRoles = (externalRoleIDs: string[]) => {
        this._linkedRoles.filter(role => externalRoleIDs.includes(role.ID)).forEach(role => {
            UserActions.editCurrentObjectAddToList(UserConstants.USER_ADD_FORM, 'RoleIDs', role.ID);
        });
    }

    private _removeExistLinkedRoles = () => {
        if (this.state.editUser) {
            this._linkedRoles.filter(role => this.state.editUser.RoleIDs.includes(role.ID)).forEach(role => {
                UserActions.editCurrentObjectRemoveFromList(UserConstants.USER_ADD_FORM, 'RoleIDs', role.ID);
            });
        }
    }

    private _checkForUnsavedChanges = (event: any) => {
        if (UserStore.getHasChanges()) {
            event.returnValue = InovxLoader.t('common:msgBox.browserSaveChanges');
        };
    }

    componentDidMount() {
        UserStore.addChangeListener(this._loadState);
        SystemStore.addChangeListener(this._systemChange);
        window.addEventListener('beforeunload', this._checkForUnsavedChanges);
        if (this.state.isEditing) {
            if (this.state.authenticationMode == AuthenticationModeEnum.Mixed && this.state.editUser && !this.state.editUser.ExternalAccountID)
                _useExternalAccount = false;
            else if (this.state.authenticationMode == AuthenticationModeEnum.LocalOnly) {
                _useExternalAccount = false;
            }
            this.setState({ useExternalAccount: _useExternalAccount });
        }
        this._updateLinkedGroupsIcon();
    }

    componentDidUpdate(prevProps: UserFormProps, prevState: UserFormState) {
        this._updateLinkedGroupsIcon();
    }

    componentWillUnmount() {
        UserStore.removeChangeListener(this._loadState);
        SystemStore.removeChangeListener(this._systemChange);
        window.removeEventListener('beforeunload', this._checkForUnsavedChanges);
    }

    private _updateLinkedGroupsIcon = () => {
        var that = this;
        $('#users-security-groups .e-chips').each(function (index, value) {
            var first = $(value).children().first().text();
            that._linkedEntitlements.forEach(function (group) {
                if (group.Name === first) {
                    if ($(value).find('.glyphicon-link').length === 0) {
                        $(value).append('<span class ="glyphicon glyphicon-link" style="padding-left:4px;position:absolute;"/>');
                    }
                }
            });
        });
        $('#users-roles .e-chips').each(function (index, value) {
            var first = $(value).children().first().text();
            that._linkedRoles.forEach(function (group) {
                if (group.Name === first) {
                    if ($(value).find('.glyphicon-link').length === 0) {
                        $(value).append('<span class ="glyphicon glyphicon-link" style="padding-left:4px;position:absolute;"/>');
                    }
                }
            });
        });

    }
    render() {
        var isNativeLoginMode: boolean;
        var isAzureADLoginMode: boolean;
        var isMixedMode: boolean = false;

        if (this.state.authenticationMode == AuthenticationModeEnum.Mixed) {
            isNativeLoginMode = true;
            isAzureADLoginMode = true;
            isMixedMode = true;
        } else if (this.state.authenticationMode == AuthenticationModeEnum.ExternalOnly) {
            isNativeLoginMode = false;
            isAzureADLoginMode = true;
        } else if (this.state.authenticationMode == AuthenticationModeEnum.LocalOnly) {
            isNativeLoginMode = true;
            isAzureADLoginMode = false;
        }
        var inputObj = {
            className: 'form-control',
            type: 'text'
        }

        var user: IUserDetails = this.state.editUser;
        var passwordInput = {
            id: 'userApp-password-input',
            className: 'form-control',
            type: 'password',
            name: 'VPass',
            value: user.Password
        };

        var firstNameInput = {
            ...inputObj,
            id: 'userApp-firstName-input',
            name: 'FirstName',
            value: (user.FirstName != null) ? user.FirstName : ""
        };

        var lastNameInput = {
            ...inputObj,
            id: 'userApp-lastName-input',
            name: 'LastName',
            value: (user.LastName != null) ? user.LastName : ""
        };

        var adUserInput = {
            ...inputObj,
            id: 'userApp-adUserName-input',
            name: 'ExternalAccountUserName',
            value: user.ExternalAccountUserName
        };

        var emailInput = {
            ...inputObj,
            id: 'userApp-email-input',
            name: 'Email',
            value: (user.Email != null) ? user.Email : ""
        };

        var header = this.state.isAdding ? InovxLoader.t('users:header.newRegisteredUser') : InovxLoader.t('users:header.editRegisterUser');
        var passwordSettings = InovxLoader.t('users:form.vsuitePasswordTooltip');
        var settings = UserStore.getEditSettings();
        passwordSettings = passwordSettings.concat(InovxLoader.t("users:form.pwdSettingsPart1").replace("{0}", settings.PasswordCharacters));
        passwordSettings = passwordSettings.concat(InovxLoader.t("users:form.pwdSettingsPart2").replace("{0}", settings.PasswordSymbols));
        passwordSettings = passwordSettings.concat(InovxLoader.t("users:form.pwdSettingsPart3").replace("{0}", settings.PasswordNumbers));
        var usernameLabel = InovxLoader.t("users:common.username");
        var usernameToolTip = InovxLoader.t("users:common.usernameTooltip")
        var checkboxLabel = InovxLoader.t("users:form.azureADAuthentication");
        var userExternalAccount: boolean = this.state.useExternalAccount;

        if (this.state.externalProviderName == ExternalProviderEnum.ADFS) {
            usernameLabel = InovxLoader.t("users:form.adfsUsername");
            usernameToolTip = InovxLoader.t("users:form.adfsUsernameTooltip")
            checkboxLabel = InovxLoader.t("users:form.adfsAuthentication");
        } else if (this.state.externalProviderName == ExternalProviderEnum.AzureAD) {
            usernameLabel = InovxLoader.t("users:form.azureUsername");
            usernameToolTip = InovxLoader.t("users:form.azureUsernameTooltip");
            checkboxLabel = InovxLoader.t("users:form.azureADAuthentication");
        }

        return (
            <InovxControls.StaticHeaderForm title={header} saveOnClick={this._saveUser} saveBtnId="userApp-save-btn" cancelOnClick={this._cancel} cancelBtnId="userApp-cancel-btn"
                isActionInProgress={this.state.isActionInProgress} inProgressMessage={this.state.actionInProgressMsg} >
                <div className='user-form'>
                    <InovxControls.If condition={isAzureADLoginMode} class='fc-margin'>
                        {isNativeLoginMode && this.state.isAdding &&
                            <div className="form-field">
                                <label className="control-label">
                                    <input type="checkbox" onChange={this._onUseExternalAccountChange} checked={userExternalAccount} />{" " + checkboxLabel}
                                </label>
                            </div>
                        }

                        {userExternalAccount &&
                            <InovxControls.FormField title={`${usernameLabel}*`} tooltip={usernameToolTip} inputName='ExternalAccountUserName'>
                                <InovxControls.ValidationInput {...adUserInput} className="form-control" onChange={this._onChange} validation={this.state.validation['ExternalAccountUserName']} readOnly={true} />
                                <a id="userApp-checkName-btn" className="btn btn-info" onClick={this._checkName} >{InovxLoader.t("users:form.searchDirectory")} </a>
                            </InovxControls.FormField>
                        }
                    </InovxControls.If>

                    {(this.state.isValidADUserID || (!this.state.useExternalAccount && isMixedMode) || (!isMixedMode && isNativeLoginMode)) &&
                        <div>
                            <div className="row">
                                <div className="col-md-6 field-col">
                                    <InovxControls.FormField title={`${InovxLoader.t("users:form.email")}*`} inputName='Email'>
                                        <InovxControls.ValidationInput {...emailInput} className="form-control" onChange={this._onChange} validation={this.state.validation['Email']} readOnly={false} autocomplete="email" />
                                    </InovxControls.FormField>
                                </div>
                                {isNativeLoginMode &&
                                    <div className="col-md-6 field-col">
                                        <InovxControls.If condition={this.state.isEditing} class='form-field'>
                                            <InovxControls.FormField title={`${InovxLoader.t("users:form.vsuitePassword")}`} tooltip={passwordSettings} inputName='VPass' >
                                                <a id="userApp-password-reset-btn" className="btn btn-info" onClick={this._onChangePassword}>{InovxLoader.t("users:form.resetPassword")}</a>
                                                <InovxControls.If condition={user.PasswordExpired}>
                                                    <h5 style={{ color: 'red' }}>{InovxLoader.t("users:form.passwordExpired")}</h5>
                                                </InovxControls.If>
                                            </InovxControls.FormField>
                                        </InovxControls.If>

                                        <InovxControls.If condition={this.state.isAdding} class='form-field'>
                                            <InovxControls.FormField title={`${InovxLoader.t("users:form.vsuitePassword")}*`} tooltip={passwordSettings} inputName='VPassword' >
                                                <InovxControls.ValidationInput {...passwordInput} className="form-control" onChange={this._onChange} validation={this.state.validation['Password']} readOnly={false} autocomplete="new-password" />
                                            </InovxControls.FormField>
                                        </InovxControls.If>
                                    </div>
                                }
                            </div>

                            <InovxControls.If condition={this.state.hasEntitlementPermission} class='fc-margin'>
                                <InovxControls.FormField title={InovxLoader.t("users:form.securityGroups")} inputName='Entitlement'>
                                    <InovxControls.MultiSelect id="users-security-groups" displayProp="Name" minLength={0} onSelect={this._onEntitlementSelect} datasource={this.state.entitlement} keyProp="ID"
                                        valueProp="ID" values={this.state.editUser.EntitlementGroupIDs} preventInternalSelect={true} canSelect={this._canSelectEntitlement} />
                                </InovxControls.FormField>
                            </InovxControls.If>

                            <InovxControls.If condition={this.state.hasRolePermission} class='fc-margin'>
                                <InovxControls.FormField title={InovxLoader.t("users:form.roles")} inputName='Role'>
                                    <InovxControls.MultiSelect id="users-roles" displayProp="Name" minLength={0} onSelect={this._onRolesSelect} datasource={this.state.roles} keyProp="ID"
                                        valueProp="ID" values={this.state.editUser.RoleIDs} preventInternalSelect={true} canSelect={this._canSelectRole} />
                                </InovxControls.FormField>
                            </InovxControls.If>

                            <InovxControls.FormField title={`${InovxLoader.t("users:form.userStatus")}: `} inputName=''>
                                <label className="radio-inline">
                                    <input type="radio" value="true" name="Enabled" onChange={this._onCheckChange} checked={user.Enabled} />
                                    {InovxLoader.t("users:common.enabled")}
                                </label>
                                <label className="radio-inline">
                                    <input type="radio" value="false" name="Enabled" onChange={this._onCheckChange} checked={!user.Enabled} />
                                    {InovxLoader.t("users:common.disabled")}
                                </label>
                            </InovxControls.FormField>

                            <InovxControls.GroupBox title={InovxLoader.t('users:form.userProfile')} class="gb-password-complexity">
                                <div className="row">
                                    <div className="col-md-6 field-col">
                                        <InovxControls.FormField title={InovxLoader.t("users:common.firstname")} inputName='FirstName'>
                                            <InovxControls.ValidationInput {...firstNameInput} className="form-control" onChange={this._onChange} validation={this.state.validation['FirstName']} readOnly={false} />
                                        </InovxControls.FormField>
                                    </div>
                                    <div className="col-md-6 field-col">
                                        <InovxControls.FormField title={InovxLoader.t("users:common.lastname")} inputName='LastName'>
                                            <InovxControls.ValidationInput {...lastNameInput} className="form-control" onChange={this._onChange} validation={this.state.validation['LastName']} readOnly={false} />
                                        </InovxControls.FormField>
                                    </div>
                                </div>
                            </InovxControls.GroupBox>

                            <InovxControls.If condition={this.state.editUser.MaxLoginAttempts} class='fc-margin'>
                                <InovxControls.FormField title={`${InovxLoader.t("users:form.resetLoginAttempts")}: `} inputName=''>
                                    <a className="btn btn-info" onClick={this._onResetLoginAttempts}>{InovxLoader.t("users:form.resetLoginAttemptsBtn")}</a>
                                </InovxControls.FormField>
                            </InovxControls.If>
                        </div>
                    }

                    <InovxControls.ModalPopUp isOpen={this.state.displaySelectUser} >
                        <UserSelect onSelect={this._fillUser} onCancel={this._closeSelectUser} />
                    </InovxControls.ModalPopUp>

                    <InovxControls.ModalPopUp isOpen={this.state.displayPasswordChange} >
                        <ChangeUserPassword User={this.state.editUser} onPasswordChanged={this._closeChangePassword} onCancel={this._closeChangePassword} />
                    </InovxControls.ModalPopUp>

                </div>
            </InovxControls.StaticHeaderForm>
        );
    }
}

export default UserForm;
